import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Power, PowerOff } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';

const TIPOS_FORMA_PAGO = [
  { value: 'efectivo_bs', label: 'Efectivo Bs' },
  { value: 'efectivo_usd', label: 'Efectivo $' },
  { value: 'pos', label: 'Punto de venta (POS)' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'pago_movil', label: 'Pago móvil' },
  { value: 'delivery', label: 'Delivery / plataforma' },
  { value: 'otro', label: 'Otro' }
];

const TIPO_LABEL = {
  ...Object.fromEntries(TIPOS_FORMA_PAGO.map((t) => [t.value, t.label])),
  cuenta_por_cobrar: 'Cuenta por Cobrar'
};
// Tipos donde tiene sentido asociar una cuenta bancaria (dónde cae el dinero).
const TIPOS_CON_CUENTA = ['pos', 'transferencia', 'pago_movil', 'delivery', 'otro'];

const CAJERO_VACIO = { nombre: '', pin: '' };
const FORMA_VACIA = { nombre: '', tipo: 'efectivo_bs', moneda: 'BS', cuenta_id: '', orden: 0 };
const PLATAFORMA_VACIA = { nombre: '', orden: 0 };

// Cabecera de tarjeta con botón "agregar" (solo ícono, como el resto del sistema).
function CabeceraSeccion({ titulo, onAgregar, textoAgregar }) {
  return (
    <div className="flex items-center justify-between p-4 border-b">
      <h2 className="font-semibold text-slate-700">{titulo}</h2>
      <button
        onClick={onAgregar}
        title={textoAgregar}
        className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

// soloToggle: la forma de pago "Cuenta por Cobrar" la administra el sistema
// (ver backend MENSAJE_CXC_SISTEMA) -- no se puede editar ni eliminar, solo
// activar/desactivar si la empresa no quiere ofrecerla en Caja.
function AccionesFila({ onEditar, onToggle, activo, onEliminar, soloToggle }) {
  return (
    <div className="flex items-center justify-end gap-3">
      {soloToggle && <span className="text-[11px] text-slate-400">del sistema</span>}
      {!soloToggle && (
        <button onClick={onEditar} title="Editar" className="text-slate-400 hover:text-primary-600 transition-colors">
          <Pencil size={15} />
        </button>
      )}
      <button
        onClick={onToggle}
        title={activo ? 'Desactivar' : 'Activar'}
        className={`transition-colors ${activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}`}
      >
        {activo ? <PowerOff size={15} /> : <Power size={15} />}
      </button>
      {!soloToggle && (
        <button onClick={onEliminar} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}

function EstadoPill({ activo }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${activo ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  );
}

function ModalForm({ titulo, onCerrar, onGuardar, error, children }) {
  return (
    <Modal title={titulo} onClose={onCerrar} maxWidth="max-w-md">
      <form onSubmit={onGuardar} className="space-y-3">
        {children}
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button type="submit" className="bg-primary-600 text-white rounded-md py-2 w-full hover:bg-primary-700">
          Guardar
        </button>
      </form>
    </Modal>
  );
}

const inputCls = 'border rounded-md px-3 py-2 w-full';

const PESTANAS = [
  { key: 'cajeros', label: 'Cajeros' },
  { key: 'formas', label: 'Formas de pago' },
  { key: 'plataformas', label: 'Plataformas' }
];

export default function CajaConfiguracion() {
  const { empresaActiva, empresaActivaNombre } = useAuth();

  const [cajeros, setCajeros] = useState([]);
  const [formas, setFormas] = useState([]);
  const [plataformas, setPlataformas] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  // Estado de edición: { entidad: 'cajero'|'forma'|'plataforma', id: number|null }
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [pestana, setPestana] = useState('cajeros');

  async function cargar() {
    const [rc, rf, rp, rcta] = await Promise.all([
      api.get('/caja/catalogos/cajeros'),
      api.get('/caja/catalogos/formas-pago'),
      api.get('/caja/catalogos/plataformas'),
      api.get('/cuentas')
    ]);
    setCajeros(rc.data);
    setFormas(rf.data);
    setPlataformas(rp.data);
    setCuentas(rcta.data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  function abrir(entidad, registro) {
    setError('');
    if (entidad === 'cajero') {
      setForm(registro ? { nombre: registro.nombre, pin: '' } : { ...CAJERO_VACIO });
    } else if (entidad === 'forma') {
      setForm(registro
        ? { nombre: registro.nombre, tipo: registro.tipo, moneda: registro.moneda, cuenta_id: registro.cuenta_id || '', orden: registro.orden }
        : { ...FORMA_VACIA });
    } else {
      setForm(registro ? { nombre: registro.nombre, orden: registro.orden } : { ...PLATAFORMA_VACIA });
    }
    setEditando({ entidad, id: registro?.id ?? null, cuentaPrevia: registro?.cuenta_id ?? null });
  }

  function cerrar() {
    setEditando(null);
    setForm({});
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    const { entidad, id } = editando;
    const rutas = { cajero: 'cajeros', forma: 'formas-pago', plataforma: 'plataformas' };
    const base = `/caja/catalogos/${rutas[entidad]}`;
    try {
      const payload = { ...form };
      if (entidad === 'cajero' && id && !payload.pin) delete payload.pin; // no cambiar PIN si se deja vacío
      if (id) await api.put(`${base}/${id}`, payload);
      else await api.post(base, payload);
      cerrar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
    }
  }

  async function toggle(entidad, registro) {
    const rutas = { cajero: 'cajeros', forma: 'formas-pago', plataforma: 'plataformas' };
    try {
      await api.put(`/caja/catalogos/${rutas[entidad]}/${registro.id}`, { activo: registro.activo ? 0 : 1 });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  }

  async function eliminar(entidad, registro) {
    if (!window.confirm(`¿Eliminar "${registro.nombre}"?`)) return;
    const rutas = { cajero: 'cajeros', forma: 'formas-pago', plataforma: 'plataformas' };
    try {
      await api.delete(`/caja/catalogos/${rutas[entidad]}/${registro.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar');
    }
  }

  const NOMBRE_ENTIDAD = { cajero: 'cajero', forma: 'forma de pago', plataforma: 'plataforma' };
  const tituloModal = editando
    ? editando.id
      ? `Editando ${NOMBRE_ENTIDAD[editando.entidad]}`
      : `${editando.entidad === 'cajero' ? 'Nuevo' : 'Nueva'} ${NOMBRE_ENTIDAD[editando.entidad]}`
    : '';

  const tipoForm = form.tipo;
  const mostrarCuenta = editando?.entidad === 'forma' && TIPOS_CON_CUENTA.includes(tipoForm);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">
        Caja · Configuración
        {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
        <BotonAyuda />
      </h1>

      <div className="flex gap-1 border-b">
        {PESTANAS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPestana(p.key)}
            className={`px-3.5 py-2.5 text-sm ${pestana === p.key ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Cajeros */}
      {pestana === 'cajeros' && (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <CabeceraSeccion titulo="Cajeros" onAgregar={() => abrir('cajero')} textoAgregar="Nuevo cajero" />
        <p className="px-4 pt-3 text-xs text-slate-400">
          Cada cajero se identifica con un PIN de 4 dígitos sobre la sesión compartida del local.
        </p>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3">Nombre</th>
              <th className="p-3 w-24">Estado</th>
              <th className="p-3 w-28 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cajeros.map((c) => (
              <tr key={c.id} className="border-t">
                <td className="p-3 font-medium">{c.nombre}</td>
                <td className="p-3"><EstadoPill activo={c.activo} /></td>
                <td className="p-3">
                  <AccionesFila
                    activo={c.activo}
                    onEditar={() => abrir('cajero', c)}
                    onToggle={() => toggle('cajero', c)}
                    onEliminar={() => eliminar('cajero', c)}
                  />
                </td>
              </tr>
            ))}
            {cajeros.length === 0 && (
              <tr><td colSpan={3} className="p-6 text-center text-slate-400">No hay cajeros creados todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Formas de pago */}
      {pestana === 'formas' && (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <CabeceraSeccion titulo="Formas de pago" onAgregar={() => abrir('forma')} textoAgregar="Nueva forma de pago" />
        <p className="px-4 pt-3 text-xs text-slate-400">
          Un POS = una cuenta bancaria (varios terminales del mismo banco van en un solo renglón).
        </p>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3">Nombre</th>
              <th className="p-3">Tipo</th>
              <th className="p-3 w-20">Moneda</th>
              <th className="p-3">Cuenta</th>
              <th className="p-3 w-24">Estado</th>
              <th className="p-3 w-28 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {formas.map((f) => (
              <tr key={f.id} className="border-t">
                <td className="p-3 font-medium">{f.nombre}</td>
                <td className="p-3 text-slate-600">{TIPO_LABEL[f.tipo] || f.tipo}</td>
                <td className="p-3">{f.moneda}</td>
                <td className="p-3 text-slate-600">{f.cuenta_nombre || '—'}</td>
                <td className="p-3"><EstadoPill activo={f.activo} /></td>
                <td className="p-3">
                  <AccionesFila
                    activo={f.activo}
                    onEditar={() => abrir('forma', f)}
                    onToggle={() => toggle('forma', f)}
                    onEliminar={() => eliminar('forma', f)}
                    soloToggle={f.tipo === 'cuenta_por_cobrar'}
                  />
                </td>
              </tr>
            ))}
            {formas.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">No hay formas de pago creadas todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Plataformas de delivery */}
      {pestana === 'plataformas' && (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <CabeceraSeccion titulo="Plataformas de delivery" onAgregar={() => abrir('plataforma')} textoAgregar="Nueva plataforma" />
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3">Nombre</th>
              <th className="p-3 w-24">Estado</th>
              <th className="p-3 w-28 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plataformas.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="p-3 font-medium">{p.nombre}</td>
                <td className="p-3"><EstadoPill activo={p.activo} /></td>
                <td className="p-3">
                  <AccionesFila
                    activo={p.activo}
                    onEditar={() => abrir('plataforma', p)}
                    onToggle={() => toggle('plataforma', p)}
                    onEliminar={() => eliminar('plataforma', p)}
                  />
                </td>
              </tr>
            ))}
            {plataformas.length === 0 && (
              <tr><td colSpan={3} className="p-6 text-center text-slate-400">No hay plataformas creadas todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {editando && (
        <ModalForm titulo={tituloModal} onCerrar={cerrar} onGuardar={guardar} error={error}>
          {editando.entidad === 'cajero' && (
            <>
              <input
                required autoFocus placeholder="Nombre del cajero"
                value={form.nombre || ''}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className={inputCls}
              />
              <input
                inputMode="numeric" pattern="\d{4}" maxLength={4}
                placeholder={editando.id ? 'PIN nuevo (4 dígitos, vacío = sin cambio)' : 'PIN (4 dígitos)'}
                value={form.pin || ''}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                className={inputCls}
                required={!editando.id}
              />
            </>
          )}

          {editando.entidad === 'forma' && (
            <>
              <input
                required autoFocus placeholder="Nombre (ej. POS Mercantil)"
                value={form.nombre || ''}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className={inputCls}
              />
              <select
                value={form.tipo}
                onChange={(e) => {
                  const tipo = e.target.value;
                  const moneda = tipo === 'efectivo_usd' ? 'USD' : 'BS';
                  setForm({ ...form, tipo, moneda, cuenta_id: TIPOS_CON_CUENTA.includes(tipo) ? form.cuenta_id : '' });
                }}
                className={inputCls}
              >
                {TIPOS_FORMA_PAGO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <label className="block text-xs text-slate-500">
                Moneda en que se teclea lo tendido
                <select
                  value={form.moneda}
                  onChange={(e) => setForm({ ...form, moneda: e.target.value })}
                  className={`${inputCls} mt-1`}
                >
                  <option value="BS">Bs</option>
                  <option value="USD">$</option>
                </select>
              </label>
              {mostrarCuenta && (
                <label className="block text-xs text-slate-500">
                  Cuenta bancaria donde cae (opcional)
                  <select
                    value={form.cuenta_id || ''}
                    onChange={(e) => setForm({ ...form, cuenta_id: e.target.value })}
                    className={`${inputCls} mt-1`}
                  >
                    <option value="">— Sin cuenta —</option>
                    {/* Las cuentas inactivas no reciben ventas nuevas (#147); sólo se
                        muestra la que la forma de pago ya tenía, para no perderla. */}
                    {cuentas
                      .filter((c) => Number(c.activo) === 1 || String(c.id) === String(editando?.cuentaPrevia))
                      .map((c) => <option key={c.id} value={c.id}>{c.nombre}{Number(c.activo) === 1 ? '' : ' (inactiva)'}</option>)}
                  </select>
                </label>
              )}
              <label className="block text-xs text-slate-500">
                Orden en el cobro
                <input
                  type="number"
                  value={form.orden ?? 0}
                  onChange={(e) => setForm({ ...form, orden: e.target.value })}
                  className={`${inputCls} mt-1`}
                />
              </label>
            </>
          )}

          {editando.entidad === 'plataforma' && (
            <>
              <input
                required autoFocus placeholder="Nombre (ej. Yummy)"
                value={form.nombre || ''}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className={inputCls}
              />
              <label className="block text-xs text-slate-500">
                Orden
                <input
                  type="number"
                  value={form.orden ?? 0}
                  onChange={(e) => setForm({ ...form, orden: e.target.value })}
                  className={`${inputCls} mt-1`}
                />
              </label>
            </>
          )}
        </ModalForm>
      )}
    </div>
  );
}
